import React from 'react';

import PropTypes from 'prop-types';

import {
    Avatar,
    CalendarIcon,
    ClockIcon,
    GemIcon,
    Grid,
    GridItem,
    HeartIcon,
    BusIcon,
    ListAdornment,
    ListBody,
    ListItem,
    PinIcon,
    Section,
    Typography,
} from 'wanderu-components';
import { localize } from 'wanderu-i18n';

import styles from './JourneyInfo.scss';

function TableBody(props) {
    return (
        <table className={styles.table}>
            <tbody {...props} />
        </table>
    );
}

function getUnitAbbreviation(distance = '') {
    if (!distance.includes('(') || !distance.includes(')')) {
        return;
    }

    return distance.split('(')[1].replace(')', '');
}

function JourneyInfo(props) {
    const {
        distanceInMiles,
        i18nMoney,
        journeyInfo = {},
        namespace,
        t,
        distanceInKm,
    } = props;

    const {
        minDurationMinutes,
        cheapestPrice,
        directTripsPerDay,
        mostOptionsCarrierName,
        numberOfCarriers,
    } = journeyInfo;

    let stats = [];

    const distanceMi = getUnitAbbreviation(`${t(`${namespace}:distanceMiles`)}`);
    const distanceKm = getUnitAbbreviation(`${t(`${namespace}:distanceKilometers`)}`);
    const distance = `${parseInt(distanceInMiles, 10)} ${distanceMi} (${parseInt(distanceInKm, 10)} ${distanceKm})`;

    if (!!distanceInMiles && !!distanceInKm) {
        stats.push({
            Icon: PinIcon,
            heading: `${namespace}:distance`,
            stat: distance,
        });
    }

    if (minDurationMinutes) {
        stats.push({
            Icon: ClockIcon,
            heading: `${namespace}:fastestTime`,
            stat: t('baseApp:trip-duration_hours', {
                hours: Math.floor(minDurationMinutes / 60),
                minutes: minDurationMinutes % 60,
            }),
        });
    }

    if (cheapestPrice) {
        stats.push({
            Icon: GemIcon,
            heading: `${namespace}:cheapestPrice`,
            stat: i18nMoney(cheapestPrice).format(),
        });
    }

    if (directTripsPerDay) {
        stats.push({
            Icon: CalendarIcon,
            heading: `${namespace}:tripsPerDay`,
            stat: directTripsPerDay,
        });
    }

    if (mostOptionsCarrierName) {
        stats.push({
            Icon: HeartIcon,
            heading: `${namespace}:mainCarrier`,
            stat: mostOptionsCarrierName,
        });
    }

    if (numberOfCarriers) {
        stats.push({
            Icon: BusIcon,
            heading: `${namespace}:carriers`,
            stat: numberOfCarriers,
        });
    }

    return (
        <Section id="quickview-title-section">
            <Typography
                className={styles.heading}
                component="h2"
                variant="h3"
            >
                {t(`${namespace}:routeQuickFacts`)}
            </Typography>
            <Typography paragraph>
                {t(`${namespace}:routeQuickSummary`)}
            </Typography>
            <Grid
                columnSpacing={6}
                columns={2}
                component={TableBody}
                rowSpacing={0}
            >
                {stats.map((item, idx) => (
                    <GridItem
                        component={ListItem}
                        componentProps={{ component: 'tr' }}
                        key={idx}
                        sm={1}
                        xs={2}
                    >
                        <ListAdornment
                            component="td"
                            aria-hidden
                        >
                            <Avatar variant="square">
                                <item.Icon />
                            </Avatar>
                        </ListAdornment>
                        <ListBody
                            component={Typography}
                            componentProps={{ component: 'td' }}
                            variant="h6"
                        >
                            {t(item.heading)}
                        </ListBody>
                        <ListAdornment
                            component={Typography}
                            componentProps={{ component: 'td' }}
                        >
                            {item.stat}
                        </ListAdornment>
                    </GridItem>
                ))}
            </Grid>
        </Section>
    );
}

JourneyInfo.propTypes = {
    distanceInMiles: PropTypes.string,
    distanceInKm: PropTypes.string,
    namespace: PropTypes.string.isRequired,
    journeyInfo: PropTypes.shape({
        minDurationMinutes: PropTypes.number,
        cheapestPrice: PropTypes.number,
        directTripsPerDay: PropTypes.number,
        mostOptionsCarrierName: PropTypes.string,
        numberOfCarriers: PropTypes.number,
    }),
};
JourneyInfo.defaultProps = {
    distanceInMiles: '',
    distanceInKm: '',
    journeyInfo: {
        minDurationMinutes: 0,
        cheapestPrice: 0,
        directTripsPerDay: 0,
        mostOptionsCarrierName: '',
        numberOfCarriers: 0,
    },
};

export {
    JourneyInfo,
    TableBody,
    getUnitAbbreviation,
};
export default localize(JourneyInfo);
